import RegistrationOrigin from "../../Components/RegistrationOrigin";
import { getClassDisplayName } from "../../utils/classPresentation";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MenuLateral from "../../Components/MenuLateral";
import { LoginContext } from "../../context/LoginContext";
import { STUDENT_ACCESS_TYPES, USER_ROLES } from "../../auth/session";
import api from "../../services/api";
import CepAddressLookup from "../../Components/CepAddressLookup";
import AuthenticatedMedia from "../../Components/AuthenticatedMedia";
import FieldLabel from "../../Components/FieldLabel";
import { fetchMediaBlob } from "../../services/media";
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowRight,
  FiCalendar,
  FiCamera,
  FiCheckCircle,
  FiEdit3,
  FiMapPin,
  FiPhone,
  FiPlus,
  FiRefreshCw,
  FiShield,
  FiUser,
} from "react-icons/fi";
import Swal from "sweetalert2";
import "./index.css";

const SITUATION_LABELS = {
  pre_sale: "Pré-venda",
  quote: "Orçamento",
  sale: "Venda",
};

const COMMERCIAL_STATUS_LABELS = {
  PEDIDO_ORCADO: "Pedido orçado",
  PEDIDO_REALIZADO: "Pedido realizado",
  VENDA_CONCLUIDA: "Venda concluída",
  PEDIDO_CANCELADO: "Pedido cancelado",
};

const SELECTION_STATUS_LABELS = {
  AWAITING_SELECTION: "Em montagem",
  ADJUSTMENT_REQUESTED: "Ajuste solicitado",
  SELECTION_SENT: "Enviada para revisão",
  SELECTION_APPROVED: "Seleção aprovada",
};

const getCommercialStatusLabel = (status) => {
  const value = String(status || "").trim();
  return COMMERCIAL_STATUS_LABELS[value.toUpperCase()]
    || SITUATION_LABELS[value.toLowerCase()]
    || "Pedido";
};

const getSelectionStatusLabel = (status) => {
  const value = String(status || "").trim().toUpperCase();
  return SELECTION_STATUS_LABELS[value] || "Seleção em acompanhamento";
};

const STUDENT_STATUS_LABELS = {
  active: "Ativo",
  inactive: "Inativo",
  suspended: "Suspenso",
};

const TIMELINE_LABELS = {
  student_created: "Cadastro",
  reference_photo: "Reconhecimento",
  event_participation: "Evento",
  sale: "Comercial",
};

const getStudentStatusLabel = (status) =>
  STUDENT_STATUS_LABELS[String(status || "").toLowerCase()] || "Estado não informado";

const EMPTY_REFERENCE_PHOTOS = [];
const REFERENCE_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/tiff",
  "image/webp",
]);

const isReferenceAttentionRequired = (reference) =>
  ["INVALID", "UNAVAILABLE"].includes(reference?.faceValidationStatus);

const getReferenceAttentionMessage = (reference) => {
  if (reference?.faceValidationMessage) return reference.faceValidationMessage;
  if (reference?.faceValidationStatus === "UNAVAILABLE") {
    return "A validacao facial desta referencia nao ficou disponivel. Substitua a foto se o problema continuar.";
  }
  return "Esta referencia nao foi aceita como uma face valida e pode prejudicar a separacao das fotos.";
};

const REFERENCE_STATUS_LABELS = {
  VALID: "Pronta para reconhecimento",
  INVALID: "Precisa de atenção",
  UNAVAILABLE: "Validação indisponível",
};

const getReferenceStatusLabel = (status) =>
  REFERENCE_STATUS_LABELS[status] || "Aguardando validação";

function AlunoPerfil() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    LoggedUser,
    activeStudentId,
    studentAccesses = [],
    selectActiveStudent,
    reconcileStudentContext,
  } = useContext(LoginContext);
  const isStudentUser = LoggedUser?.role === USER_ROLES.USER;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [buyerOrders, setBuyerOrders] = useState([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [referencePreviews, setReferencePreviews] = useState({});
  const [referenceReplacingId, setReferenceReplacingId] = useState("");
  const [referenceAdding, setReferenceAdding] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
    mobilePhone: "",
    nickname: "",
    birthDate: "",
    zipCode: "",
    street: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    uf: "",
  });
  const lastReconciledStudentId = useRef("");
  const referenceFileInputRef = useRef(null);
  const referencePhotos = profile?.student?.referencePhotos || EMPTY_REFERENCE_PHOTOS;
  const referencePreviewKey = referencePhotos
    .map((reference) => `${reference.id}:${reference.updatedAt || ""}`)
    .join("|");
  const canViewReferencePhotos =
    profile?.permissions?.canViewReferencePhotos === true;

  const mensagemDoErro = useCallback((error) => {
    const msg = error?.response?.data?.message;
    if (Array.isArray(msg)) return msg.join(", ");
    if (typeof msg === "string") return msg;
    return error?.message || "Erro ao comunicar com a API";
  }, []);

  const carregarPerfil = useCallback(async () => {
    if (isStudentUser && !activeStudentId) {
      setProfile(null);
      setProfileError("Nenhum perfil autorizado esta disponivel no momento.");
      return;
    }

    setLoading(true);
    setProfileError("");
    try {
      const endpoint = isStudentUser ? "/students/me/profile" : `/students/${id}/profile`;
      const resp = isStudentUser
        ? await api.get(endpoint, { params: { studentId: activeStudentId } })
        : await api.get(endpoint);
      setProfile(resp.data);
      const studentData = resp.data?.student || {};
      const personData = resp.data?.person || studentData;
      const accountData = resp.data?.account || studentData;
      setProfileForm({
        name: personData.name || "",
        email: accountData.email || "",
        phone: personData.phone || "",
        mobilePhone: personData.mobilePhone || "",
        nickname: personData.nickname || "",
        birthDate: personData.birthDate || "",
        zipCode: personData.zipCode || "",
        street: personData.street || "",
        number: personData.number || "",
        complement: personData.complement || "",
        district: personData.district || "",
        city: personData.city || "",
        uf: personData.uf || "",
      });
    } catch (err) {
      const isRevokedContext =
        isStudentUser && err?.response?.status === 403 && activeStudentId;

      if (
        isRevokedContext &&
        lastReconciledStudentId.current !== activeStudentId
      ) {
        lastReconciledStudentId.current = activeStudentId;
        const refreshedAccesses = await reconcileStudentContext();

        if (
          !refreshedAccesses.some(
            (access) => access.studentId === activeStudentId,
          )
        ) {
          setProfile(null);
          setProfileError(
            "O perfil selecionado nao esta mais autorizado. A lista de perfis foi atualizada.",
          );
          return;
        }
      }

      setProfile(null);
      setProfileError(mensagemDoErro(err));
    } finally {
      setLoading(false);
    }
  }, [
    activeStudentId,
    id,
    isStudentUser,
    mensagemDoErro,
    reconcileStudentContext,
  ]);

  const carregarPedidos = useCallback(async () => {
    if (!isStudentUser) {
      setBuyerOrders([]);
      return;
    }
    try {
      const response = await api.get("/sales/mine", {
        params: { page: 1, limit: 100 },
      });
      setBuyerOrders(response.data?.orders || []);
    } catch {
      setBuyerOrders([]);
    }
  }, [isStudentUser]);

  useEffect(() => {
    lastReconciledStudentId.current = "";
  }, [activeStudentId]);

  useEffect(() => {
    if (!canViewReferencePhotos || !profile?.student?.id || !referencePhotos.length) {
      setReferencePreviews({});
      return undefined;
    }

    let active = true;
    const createdUrls = [];

    const loadReferencePreviews = async () => {
      const previews = {};
      await Promise.all(
        referencePhotos.map(async (reference) => {
          try {
            const blob = await fetchMediaBlob(
              `/media/student-reference-photos/${reference.id}`,
            );
            const objectUrl = URL.createObjectURL(blob);
            createdUrls.push(objectUrl);
            previews[reference.id] = objectUrl;
          } catch (error) {
            // A foto continua protegida se a leitura autenticada nao estiver disponivel.
          }
        }),
      );
      if (active) setReferencePreviews(previews);
    };

    loadReferencePreviews();

    return () => {
      active = false;
      createdUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    };
  }, [canViewReferencePhotos, profile?.student?.id, referencePhotos, referencePreviewKey]);

  const atualizarProfileForm = (field, value) => {
    setProfileForm((current) => ({ ...current, [field]: value }));
  };

  const aplicarEnderecoPorCep = (address) => {
    setProfileForm((current) => ({
      ...current,
      street: address.street,
      district: address.district,
      city: address.city,
      uf: address.uf,
    }));
  };

  const salvarMeuPerfil = async () => {
    if (!profile?.permissions?.canEditProfile) return;

    setProfileSaving(true);
    try {
      const { email: _email, ...personalProfileForm } = profileForm;
      const resp = await api.patch("/students/me/profile", personalProfileForm);
      setProfile(resp.data);
      const studentData = resp.data?.student || {};
      const personData = resp.data?.person || studentData;
      const accountData = resp.data?.account || studentData;
      setProfileForm({
        name: personData.name || "",
        email: accountData.email || "",
        phone: personData.phone || "",
        mobilePhone: personData.mobilePhone || "",
        nickname: personData.nickname || "",
        birthDate: personData.birthDate || "",
        zipCode: personData.zipCode || "",
        street: personData.street || "",
        number: personData.number || "",
        complement: personData.complement || "",
        district: personData.district || "",
        city: personData.city || "",
        uf: personData.uf || "",
      });
      setEditingProfile(false);
      Swal.fire("Atualizado", "Perfil atualizado com sucesso.", "success");
    } catch (err) {
      Swal.fire("Erro", mensagemDoErro(err), "error");
    } finally {
      setProfileSaving(false);
    }
  };

  useEffect(() => {
    carregarPerfil();
  }, [carregarPerfil]);


  useEffect(() => {
    carregarPedidos();
  }, [carregarPedidos]);

  const student = profile?.student;
  const profilePermissions = profile?.permissions || {};
  const isOwnStudentProfile =
    isStudentUser &&
    profilePermissions.accessType === STUDENT_ACCESS_TYPES.STUDENT &&
    profilePermissions.canEditProfile === true;
  const isResponsibleProfile =
    isStudentUser &&
    profilePermissions.accessType === STUDENT_ACCESS_TYPES.RESPONSIBLE;
  const person = profile?.person ||
    (isOwnStudentProfile
      ? {
          name: student?.name,
          phone: student?.phone,
          mobilePhone: student?.mobilePhone,
          nickname: student?.nickname,
          birthDate: student?.birthDate,
          zipCode: student?.zipCode,
          street: student?.street,
          number: student?.number,
          complement: student?.complement,
          district: student?.district,
          city: student?.city,
          uf: student?.uf,
        }
      : { name: student?.name });
  const account = isOwnStudentProfile
    ? profile?.account || { email: student?.email, status: student?.status }
    : null;
  const fullAddress = [
    person?.street,
    person?.number,
    person?.complement,
    person?.district,
    person?.city,
    person?.uf,
    person?.zipCode,
  ]
    .filter(Boolean)
    .join(", ");
  const avatarPath = profilePermissions.canViewReferencePhotos
    ? student?.photoProfileMedia?.path
    : undefined;
  const initials = (person?.name || "Aluno")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  const iniciarAdicaoReferencia = () => {
    if (!canViewReferencePhotos || referencePhotos.length >= 3 || referenceReplacingId || referenceAdding) {
      if (referencePhotos.length >= 3) {
        Swal.fire("Limite atingido", "O aluno já possui o máximo de 3 fotos de referência.", "info");
      }
      return;
    }
    setReferenceAdding(true);
    if (referenceFileInputRef.current) {
      referenceFileInputRef.current.value = "";
      referenceFileInputRef.current.click();
    }
  };

  const iniciarSubstituicaoReferencia = (referenceId) => {
    if (!canViewReferencePhotos || referenceReplacingId || referenceAdding) return;
    setReferenceReplacingId(referenceId);
    if (referenceFileInputRef.current) {
      referenceFileInputRef.current.value = "";
      referenceFileInputRef.current.click();
    }
  };

  const substituirReferenciaSelecionada = async (event) => {
    const file = event.target.files?.[0];
    const referenceId = referenceReplacingId;
    const addingReference = referenceAdding;
    setReferenceReplacingId("");
    setReferenceAdding(false);

    if (!file || (!referenceId && !addingReference) || !profile?.student?.id) return;
    if (!REFERENCE_IMAGE_MIME_TYPES.has(file.type)) {
      Swal.fire(
        "Arquivo invalido",
        "Selecione JPEG, PNG, TIFF ou WebP para substituir a referencia.",
        "warning",
      );
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = addingReference
        ? await api.post(
            `/students/${profile.student.id}/reference-photos`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
          )
        : await api.put(
            `/students/${profile.student.id}/reference-photos/${referenceId}`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
      setProfile((current) => ({
        ...current,
        student: {
          ...current.student,
          referencePhotos: response.data?.referencePhotos || [],
          recognitionSyncStatus: response.data?.recognition?.status || current.student.recognitionSyncStatus,
          recognitionSyncMessage: response.data?.recognition?.message || current.student.recognitionSyncMessage,
        },
      }));
      Swal.fire(
        addingReference ? "Foto adicionada" : "Referência atualizada",
        addingReference
          ? "A nova foto foi armazenada e já está disponível para validação facial."
          : "A foto foi armazenada e o novo estado facial foi informado de forma segura.",
        "success",
      );
    } catch (error) {
      Swal.fire("Nao foi possivel substituir", mensagemDoErro(error), "error");
    }
  };

  return (
    <div className="pagina-cad perfil-page">
      <MenuLateral />

      <main className="perfil-conteudo">
        <header className="perfil-page-heading">
          <div>
            <span className="perfil-page-kicker">
              <FiActivity aria-hidden="true" />
              {isStudentUser ? "Seu espaço no Formari" : "Consulta do aluno"}
            </span>
            <h1>{isStudentUser ? "Área do aluno" : "Perfil do aluno"}</h1>
            <p>
              {isStudentUser
                ? "Acompanhe seus pedidos, referências e informações acadêmicas em um só lugar."
                : "Consulte os dados acadêmicos, referências e histórico deste aluno."
              }
            </p>
          </div>
          {profile && (
            <div className="perfil-page-note">
              <FiShield aria-hidden="true" />
              <span>
                <strong>Dados protegidos</strong>
                <small>Você vê apenas o que está autorizado.</small>
              </span>
            </div>
          )}
        </header>

        {isStudentUser && studentAccesses.length > 1 && (
          <section className="perfil-section student-context-selector">
            <div className="section-title">
              <div>
                <h3>Perfis autorizados</h3>
                <p>Escolha qual perfil você quer acompanhar agora.</p>
              </div>
            </div>

            <label className="student-context-control">
              Perfil atual
              <select
                value={activeStudentId}
                onChange={(event) => selectActiveStudent(event.target.value)}
              >
                {studentAccesses.map((access, index) => (
                  <option key={access.studentId} value={access.studentId}>
                    Perfil {index + 1} - {access.accessType === STUDENT_ACCESS_TYPES.RESPONSIBLE ? "dependente autorizado" : "meu perfil"}
                  </option>
                ))}
              </select>
            </label>
          </section>
        )}

        {loading && (
          <div className="perfil-loading" role="status" aria-live="polite">
            <span className="perfil-loading-mark" aria-hidden="true" />
            Carregando seu perfil...
          </div>
        )}

        {!loading && profileError && (
          <div className="perfil-empty" role="alert">
            {profileError}
          </div>
        )}

        {!loading && !profile && !profileError && (
          <div className="perfil-empty">Perfil não encontrado.</div>
        )}

        {profile && (
          <>
            <section className="perfil-header" aria-labelledby="perfil-aluno-nome">
              <div className="perfil-avatar-column">
                <div className="perfil-avatar">
                  {avatarPath ? (
                    <AuthenticatedMedia path={avatarPath} alt={person.name} />
                  ) : (
                    <span>{initials}</span>
                  )}
                </div>
                <span className="perfil-avatar-caption">Perfil do aluno</span>
              </div>

              <div className="perfil-main">
                <div className="perfil-eyebrow"><FiUser aria-hidden="true" /> Perfil ativo</div>
                <h2 id="perfil-aluno-nome">{person.name || "Aluno sem nome"}</h2>
                <div className="perfil-tags">
                  <span><FiShield aria-hidden="true" /> {getStudentStatusLabel(student.status)}</span>
                  <span>{isResponsibleProfile ? "Perfil autorizado" : "Meu perfil"}</span>
                  {isOwnStudentProfile && <span className="perfil-login-meta">{account?.email || "E-mail não informado"}</span>}
                </div>

                <p className="perfil-hero-message">
                  {isResponsibleProfile
                    ? "Acompanhando um perfil autorizado com acesso somente às informações permitidas."
                    : "Este é o seu espaço para acompanhar pedidos e manter seus dados atualizados."
                  }
                </p>

                {isStudentUser && (
                  <a className="perfil-hero-action" href="#student-orders">
                    Acessar meus pedidos <FiArrowRight aria-hidden="true" />
                  </a>
                )}

                <div className="perfil-academic-grid" aria-label="Perfil acadêmico">
                  <RegistrationOrigin value={student.registrationOrigin} />
                  <div>
                    <span>Turma</span>
                    <strong style={{ overflowWrap: "anywhere" }}>{getClassDisplayName(student.class)}</strong>
                  </div>
                  <div>
                    <span>Instituição</span>
                    <strong className="perfil-institution-value">
                      {student.class?.institution?.name || "Não informada"}
                    </strong>
                  </div>
                  <div>
                    <span>Matrícula</span>
                    <strong>{student.registrationNumber || "—"}</strong>
                  </div>
                </div>
              </div>
            </section>

            {isOwnStudentProfile && (
              <section className="perfil-section person-section">
                <div className="section-title">
                  <div>
                    <h3>Pessoa</h3>
                    <p>Seus dados de contato e endereço em um só lugar.</p>
                  </div>
                  <button
                    className="section-action-button"
                    type="button"
                    onClick={() => setEditingProfile((current) => !current)}
                    disabled={profileSaving}
                  >
                    <FiEdit3 aria-hidden="true" />
                    {editingProfile ? "Cancelar" : "Editar dados"}
                  </button>
                </div>

                {editingProfile ? (
                  <div className="profile-edit-grid">
                    <FieldLabel label="Nome">
                      <input placeholder="Nome" value={profileForm.name} onChange={(event) => atualizarProfileForm("name", event.target.value)} />
                    </FieldLabel>
                    <FieldLabel label="Telefone">
                      <input placeholder="Telefone" value={profileForm.phone} onChange={(event) => atualizarProfileForm("phone", event.target.value)} />
                    </FieldLabel>
                    <FieldLabel label="Celular">
                      <input placeholder="Celular" value={profileForm.mobilePhone} onChange={(event) => atualizarProfileForm("mobilePhone", event.target.value)} />
                    </FieldLabel>
                    <FieldLabel label="Apelido">
                      <input placeholder="Apelido" value={profileForm.nickname} onChange={(event) => atualizarProfileForm("nickname", event.target.value)} />
                    </FieldLabel>
                    <FieldLabel label="Data de nascimento">
                      <input type="date" value={profileForm.birthDate || ""} onChange={(event) => atualizarProfileForm("birthDate", event.target.value)} />
                    </FieldLabel>
                    <CepAddressLookup
                      address={profileForm}
                      onZipCodeChange={(value) => atualizarProfileForm("zipCode", value)}
                      onApplyAddress={aplicarEnderecoPorCep}
                      disabled={profileSaving}
                    />
                    <FieldLabel label="Logradouro">
                      <input placeholder="Logradouro" value={profileForm.street} onChange={(event) => atualizarProfileForm("street", event.target.value)} />
                    </FieldLabel>
                    <FieldLabel label="Número">
                      <input placeholder="Numero" value={profileForm.number} onChange={(event) => atualizarProfileForm("number", event.target.value)} />
                    </FieldLabel>
                    <FieldLabel label="Complemento">
                      <input placeholder="Complemento" value={profileForm.complement} onChange={(event) => atualizarProfileForm("complement", event.target.value)} />
                    </FieldLabel>
                    <FieldLabel label="Bairro">
                      <input placeholder="Bairro" value={profileForm.district} onChange={(event) => atualizarProfileForm("district", event.target.value)} />
                    </FieldLabel>
                    <FieldLabel label="Cidade">
                      <input placeholder="Cidade" value={profileForm.city} onChange={(event) => atualizarProfileForm("city", event.target.value)} />
                    </FieldLabel>
                    <FieldLabel label="UF">
                      <input placeholder="UF" maxLength="2" value={profileForm.uf} onChange={(event) => atualizarProfileForm("uf", event.target.value.toUpperCase())} />
                    </FieldLabel>
                    <div className="profile-edit-actions">
                      <button type="button" onClick={salvarMeuPerfil} disabled={profileSaving}>
                        {profileSaving ? "Salvando..." : "Salvar perfil"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="person-facts">
                    <div className="person-fact">
                      <FiUser aria-hidden="true" />
                      <span>Nome</span>
                      <strong>{person?.name || "Nome não informado"}</strong>
                    </div>
                    <div className="person-fact">
                      <FiPhone aria-hidden="true" />
                      <span>Telefone</span>
                      <strong>{person?.phone || "Não informado"}</strong>
                    </div>
                    <div className="person-fact">
                      <FiPhone aria-hidden="true" />
                      <span>Celular</span>
                      <strong>{person?.mobilePhone || "Não informado"}</strong>
                    </div>
                    <div className="person-fact">
                      <FiCalendar aria-hidden="true" />
                      <span>Data de nascimento</span>
                      <strong>{person?.birthDate ? new Date(`${person.birthDate}T12:00:00`).toLocaleDateString("pt-BR") : "Não informada"}</strong>
                    </div>
                    <div className="person-fact person-fact-address">
                      <FiMapPin aria-hidden="true" />
                      <span>Endereço completo</span>
                      <strong>{fullAddress || "Endereço não informado"}</strong>
                    </div>
                  </div>
                )}
              </section>
            )}

            <section id="student-orders" className="perfil-section sales-section">
              <div className="section-title">
                <div>
                  <div className="section-eyebrow"><FiCamera aria-hidden="true" /> Acesso rápido</div>
                  <h3>Vendas e produtos</h3>
                  <p>Acesse um produto para escolher suas fotos ou acompanhar uma seleção já enviada.</p>
                </div>
                <span className="section-count">
                  {(buyerOrders.length || profile.sales?.length || 0)} {(buyerOrders.length || profile.sales?.length || 0) === 1 ? "pedido" : "pedidos"}
                </span>
              </div>

              <div className="sale-list">
                {(buyerOrders.length ? buyerOrders : profile.sales || []).map((sale) => (
                  <div key={sale.id} className="sale-card">
                    <div className="sale-card-heading">
                      <div>
                        <strong>{getCommercialStatusLabel(sale.commercialStatus || sale.situation)}</strong>
                        <span>{sale.createdAt ? new Date(sale.createdAt).toLocaleDateString() : sale.saleDate ? new Date(sale.saleDate).toLocaleDateString() : "-"}</span>
                      </div>
                      {sale.totalValue !== undefined ? <strong>R$ {Number(sale.totalValue || 0).toFixed(2)}</strong> : null}
                    </div>
                    <div className="sale-products">
                      {(sale.items || sale.products || []).map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          className="sale-product-button"
                          onClick={() => navigate(
                            `/SelecaoFotos?${new URLSearchParams({
                              saleId: sale.id,
                              itemId: product.id,
                            }).toString()}`,
                          )}
                        >
                          <span className="sale-product-icon" aria-hidden="true"><FiCamera /></span>
                          <span className="sale-product-copy">
                            <strong>{product.productSnapshot?.name || product.productName || "Produto"}</strong>
                            <small>
                              {product.quantity}x · {product.requiredPhotoCount !== undefined ? `${product.requiredPhotoCount} fotos` : product.selectedPhotoLimit ? `${product.selectedPhotoLimit} fotos` : "fotos conforme pedido"}
                              {product.selectionStatus ? ` · ${getSelectionStatusLabel(product.selectionStatus)}` : product.photoSelections?.length ? ` · ${product.photoSelections.length} selecionada(s)` : ""}
                            </small>
                          </span>
                          <FiArrowRight className="sale-product-arrow" aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {!(buyerOrders.length || profile.sales?.length) && (
                  <div className="perfil-empty">Nenhuma venda, orçamento ou pré-venda vinculada.</div>
                )}
              </div>
            </section>

            {isResponsibleProfile && (
              <section className="perfil-section responsible-access-panel">
                <h3>Acesso de responsável</h3>
                <p>
                  Você pode acompanhar somente o perfil acadêmico autorizado. Dados pessoais, conta, endereço e referências do dependente ficam protegidos.
                </p>
              </section>
            )}

            {(isOwnStudentProfile ||
              (referencePhotos.some(isReferenceAttentionRequired) && isResponsibleProfile) ||
              (isResponsibleProfile && ["failed", "skipped"].includes(student?.recognitionSyncStatus))) && (
              <section className="perfil-section reference-library-section" aria-label="Fotos de referência">
                <div className="section-title reference-section-heading">
                  <div>
                    <div className="section-eyebrow"><FiCamera aria-hidden="true" /> Identidade facial</div>
                    <h3>Fotos de referência</h3>
                    <p>Use de uma a três fotos nítidas para ajudar na separação das fotos dos eventos.</p>
                  </div>
                  {isOwnStudentProfile && (
                    <button
                      type="button"
                      className="reference-add-button"
                      onClick={iniciarAdicaoReferencia}
                      disabled={Boolean(referenceReplacingId || referenceAdding)}
                    >
                      <FiPlus aria-hidden="true" /> Adicionar foto
                    </button>
                  )}
                </div>

                {isOwnStudentProfile ? (
                  <>
                    <div className="reference-library-status">
                      <div className="reference-count-mark">
                        <strong>{referencePhotos.length}</strong>
                        <span>de 3 fotos</span>
                      </div>
                      <div>
                        <strong>{student?.recognitionSyncStatus === "synced" ? "Reconhecimento atualizado" : "Referências prontas para uso"}</strong>
                        <p>{student?.recognitionSyncMessage || "Você pode trocar uma foto a qualquer momento."}</p>
                      </div>
                    </div>

                    <div className="reference-photo-grid">
                      {referencePhotos.map((reference) => {
                        const needsAttention = isReferenceAttentionRequired(reference);
                        return (
                          <article className={`reference-photo-card ${needsAttention ? "needs-attention" : ""}`} key={reference.id}>
                            <div className="reference-photo-frame">
                              {referencePreviews[reference.id] ? (
                                <img
                                  src={referencePreviews[reference.id]}
                                  alt={`Foto de referência ${reference.position}`}
                                />
                              ) : (
                                <div className="reference-protected-preview" aria-label="Imagem de referência protegida">
                                  <FiShield aria-hidden="true" />
                                  Imagem protegida
                                </div>
                              )}
                              {reference.isPrimary && <span className="reference-primary-badge">Principal</span>}
                            </div>
                            <div className="reference-photo-details">
                              <div className="reference-photo-title">
                                <strong>Referência {reference.position}</strong>
                                <span className={`reference-status reference-status-${String(reference.faceValidationStatus || "pending").toLowerCase()}`}>
                                  {needsAttention ? <FiAlertTriangle aria-hidden="true" /> : <FiCheckCircle aria-hidden="true" />}
                                  {getReferenceStatusLabel(reference.faceValidationStatus)}
                                </span>
                              </div>
                              {needsAttention && <p>{getReferenceAttentionMessage(reference)}</p>}
                              <button
                                type="button"
                                className="reference-card-action"
                                onClick={() => iniciarSubstituicaoReferencia(reference.id)}
                                disabled={Boolean(referenceReplacingId || referenceAdding)}
                              >
                                <FiRefreshCw aria-hidden="true" />
                                {referenceReplacingId === reference.id ? "Escolha a nova foto..." : "Trocar foto"}
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>

                    {referencePhotos.length === 0 && (
                      <div className="reference-empty-state">
                        <FiCamera aria-hidden="true" />
                        <strong>Adicione sua primeira foto de referência</strong>
                        <span>Uma boa iluminação e o rosto voltado para a câmera ajudam na validação.</span>
                        <button type="button" onClick={iniciarAdicaoReferencia} disabled={Boolean(referenceAdding)}>
                          <FiPlus aria-hidden="true" /> Adicionar primeira foto
                        </button>
                      </div>
                    )}

                  </>
                ) : (
                  <div className="reference-responsible-alert" role="status">
                    A referência facial deste dependente precisa de atenção. Oriente o aluno a usar a própria conta ou procure um ADMIN; a imagem e a substituição permanecem protegidas.
                  </div>
                )}
                <input
                  ref={referenceFileInputRef}
                  type="file"
                  aria-label="Substituir referencia facial"
                  accept="image/jpeg,image/png,image/tiff,image/webp"
                  hidden
                  onChange={substituirReferenciaSelecionada}
                />
              </section>
            )}

            <section className="perfil-section timeline-section">
              <div className="section-title">
                <h3>Linha do tempo</h3>
              </div>

              <div className="timeline">
                {(profile.timeline || []).map((item, index) => (
                  <div key={`${item.type}-${index}`} className="timeline-item">
                    <span>{TIMELINE_LABELS[item.type] || "Registro"}</span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.description}</p>
                      <small>{item.date ? new Date(item.date).toLocaleDateString() : "-"}</small>
                    </div>
                  </div>
                ))}
                {(!profile.timeline || profile.timeline.length === 0) && (
                  <div className="perfil-empty">Nenhum histórico encontrado.</div>
                )}
              </div>
            </section>

          </>
        )}

      </main>
    </div>
  );
}

export default AlunoPerfil;
